/**
 * ThereminPanel - Interface pour le Theremin
 * Zone de jeu interactive contrôlée par la souris
 */

import { themeManager } from '../themes/ThemeManager.js';
import { WaveSelector } from '../components/WaveSelector.js';
import { PresetSelector } from '../components/PresetSelector.js';
import { THEREMIN_PRESETS } from '../../state/InstrumentPresets.js';

export class ThereminPanel {
    constructor(theremin, store) {
        this.theremin = theremin;
        this.store = store;
        this.panelId = 'theremin';
        this.isMouseDown = false;
        this.playArea = null;
        this.noteDisplay = null;
        this.freqDisplay = null;
        this.components = { knobs: {} };
    }

    isCollapsed() {
        return this.store?.isPanelCollapsed(this.panelId) || false;
    }

    render(container) {
        this.container = container;
        const isCollapsed = this.isCollapsed();
        const isPowered = this.store.getPowerState('theremin');

        this.theremin.setPower(isPowered);

        container.innerHTML = `
            <div class="machine theremin ${!isPowered ? 'powered-off' : ''} ${isCollapsed ? 'collapsed' : ''}" id="theremin_machine">
                <div class="machine-title theremin-title">
                    <button class="collapse-btn" id="theremin_collapse" title="Réduire/Agrandir">${isCollapsed ? '▶' : '▼'}</button>
                    THEREMIN
                    <span class="brand">Joué sans le toucher!</span>
                    <div id="theremin_presets"></div>
                    <button class="power-btn ${isPowered ? 'on' : ''}" id="theremin_power">⏻</button>
                </div>

                <div class="machine-content ${isCollapsed ? 'hidden' : ''}" id="theremin_content">
                <div class="theremin-content">
                    <div class="theremin-play-area" id="thereminPlayArea">
                        <div class="theremin-antenna antenna-pitch"></div>
                        <div class="theremin-antenna antenna-volume"></div>
                        <div class="theremin-crosshair" id="thereminCrosshair"></div>
                        <div class="theremin-instructions">
                            Cliquez et bougez la souris<br>
                            ← Grave | Aigu →<br>
                            ↑ Fort | Faible ↓
                        </div>
                        <div class="theremin-display">
                            <span class="theremin-note" id="thereminNote">--</span>
                            <span class="theremin-freq" id="thereminFreq">--- Hz</span>
                        </div>
                    </div>

                    <div class="theremin-controls">
                        <div class="control-section">
                            <div class="section-title">WAVE</div>
                            <div id="theremin_wave"></div>
                        </div>
                        <div class="control-section">
                            <div class="section-title">VIBRATO</div>
                            <div class="knob-group" id="theremin_vibrato"></div>
                            <div class="knob-group" id="theremin_vib_amount"></div>
                        </div>
                        <div class="control-section">
                            <div class="section-title">GLIDE</div>
                            <div class="knob-group" id="theremin_porta"></div>
                        </div>
                        <div class="control-section">
                            <div class="section-title">VOLUME</div>
                            <div class="knob-group" id="theremin_volume"></div>
                        </div>
                    </div>
                </div>
                </div><!-- /theremin-content -->
                </div><!-- /machine-content -->
            </div>
        `;

        this.setupCollapseButton();
        this.setupPowerButton();

        if (!isCollapsed) {
            this.playArea = document.getElementById('thereminPlayArea');
            this.noteDisplay = document.getElementById('thereminNote');
            this.freqDisplay = document.getElementById('thereminFreq');
            this.crosshair = document.getElementById('thereminCrosshair');

            this.setupPlayArea();
            this.createControls();
        }
    }

    setupCollapseButton() {
        const collapseBtn = document.getElementById('theremin_collapse');
        if (!collapseBtn) return;

        collapseBtn.addEventListener('click', () => {
            this.store.togglePanelCollapsed(this.panelId);
            this.destroy();
            this.render(this.container);
        });
    }

    setupPowerButton() {
        const powerBtn = document.getElementById('theremin_power');
        const machine = document.querySelector('.theremin');

        powerBtn.addEventListener('click', () => {
            const newState = !this.theremin.getPower();
            this.theremin.setPower(newState);
            this.store.setPowerState('theremin', newState);
            powerBtn.classList.toggle('on', newState);
            machine.classList.toggle('powered-off', !newState);
        });
    }

    setupPlayArea() {
        // Mouse events
        this.playArea.addEventListener('mousedown', (e) => {
            if (!this.theremin.isPowered) return;
            this.isMouseDown = true;
            this.theremin.start();
            this.updateFromMouse(e);
            this.playArea.classList.add('playing');
        });

        document.addEventListener('mouseup', () => {
            if (this.isMouseDown) {
                this.isMouseDown = false;
                this.theremin.stop();
                this.playArea.classList.remove('playing');
                this.crosshair.style.opacity = '0';
            }
        });

        document.addEventListener('mousemove', (e) => {
            if (this.isMouseDown) {
                this.updateFromMouse(e);
            }
        });

        // Touch events pour mobile
        this.playArea.addEventListener('touchstart', (e) => {
            if (!this.theremin.isPowered) return;
            e.preventDefault();
            this.isMouseDown = true;
            this.theremin.start();
            this.updateFromTouch(e);
            this.playArea.classList.add('playing');
        });

        document.addEventListener('touchend', () => {
            if (this.isMouseDown) {
                this.isMouseDown = false;
                this.theremin.stop();
                this.playArea.classList.remove('playing');
                this.crosshair.style.opacity = '0';
            }
        });

        document.addEventListener('touchmove', (e) => {
            if (this.isMouseDown) {
                this.updateFromTouch(e);
            }
        });
    }

    updateFromMouse(e) {
        const rect = this.playArea.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

        this.theremin.updatePosition(x, y);
        this.updateDisplay();
        this.updateCrosshair(x, y);
    }

    updateFromTouch(e) {
        if (e.touches.length > 0) {
            const touch = e.touches[0];
            const rect = this.playArea.getBoundingClientRect();
            const x = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
            const y = Math.max(0, Math.min(1, (touch.clientY - rect.top) / rect.height));

            this.theremin.updatePosition(x, y);
            this.updateDisplay();
            this.updateCrosshair(x, y);
        }
    }

    updateDisplay() {
        this.noteDisplay.textContent = this.theremin.getCurrentNote();
        this.freqDisplay.textContent = `${this.theremin.getCurrentFrequency()} Hz`;
    }

    updateCrosshair(x, y) {
        this.crosshair.style.left = `${x * 100}%`;
        this.crosshair.style.top = `${y * 100}%`;
        this.crosshair.style.opacity = '1';
    }

    createControls() {
        // Wave selector
        const waveContainer = document.getElementById('theremin_wave');
        this.components.wave = new WaveSelector(waveContainer, {
            waveforms: [
                { value: 'sine', label: 'SIN' },
                { value: 'triangle', label: 'TRI' },
                { value: 'sawtooth', label: 'SAW' },
                { value: 'square', label: 'SQR' }
            ],
            value: this.theremin._params.waveform
        }).onChange(wave => {
            this.theremin.setWaveform(wave);
            this._saveParams();
        });

        // Vibrato rate (3-8 Hz typique pour theremin)
        this.components.knobs.vibrato = themeManager.createKnob(
            document.getElementById('theremin_vibrato'), {
                min: 1, max: 10, value: this.theremin._params.vibrato,
                label: 'Rate', unit: 'Hz', step: 0.1
            }
        ).onChange(value => {
            this.theremin.setVibrato(value);
            this._saveParams();
        });

        // Vibrato depth (0 = off, 1 = ±15 Hz max)
        this.components.knobs.vibAmount = themeManager.createKnob(
            document.getElementById('theremin_vib_amount'), {
                min: 0, max: 1, value: this.theremin._params.vibratoAmount,
                label: 'Depth', step: 0.01
            }
        ).onChange(value => {
            this.theremin.setVibratoAmount(value);
            this._saveParams();
        });

        // Portamento
        this.components.knobs.porta = themeManager.createKnob(
            document.getElementById('theremin_porta'), {
                min: 0.01, max: 0.5, value: this.theremin._params.portamento,
                label: 'Glide', unit: 's', step: 0.01
            }
        ).onChange(value => {
            this.theremin.setPortamento(value);
            this._saveParams();
        });

        // Volume
        this.components.knobs.volume = themeManager.createKnob(
            document.getElementById('theremin_volume'), {
                min: 0, max: 1, value: this.theremin._params.volume,
                label: 'Vol', step: 0.01
            }
        ).onChange(value => {
            this.theremin.setVolume(value);
            this._saveParams();
        });

        this.setupPresetSelector();
    }

    setupPresetSelector() {
        const container = document.getElementById('theremin_presets');
        if (!container) return;

        this.presetSelector = new PresetSelector({
            presets: THEREMIN_PRESETS,
            currentPreset: 'Classic',
            onChange: (name, data) => this._loadPreset(data)
        });

        container.appendChild(this.presetSelector.element);
    }

    _loadPreset(presetData) {
        if (!presetData) return;

        this.theremin.setParams(presetData);
        this._updateKnobsFromParams(presetData);
        this._saveParams();
    }

    _updateKnobsFromParams(params) {
        if (this.components.knobs.vibrato && params.vibrato !== undefined) {
            this.components.knobs.vibrato.setValue(params.vibrato);
        }
        if (this.components.knobs.vibAmount && params.vibratoAmount !== undefined) {
            this.components.knobs.vibAmount.setValue(params.vibratoAmount);
        }
        if (this.components.knobs.porta && params.portamento !== undefined) {
            this.components.knobs.porta.setValue(params.portamento);
        }
        if (this.components.knobs.volume && params.volume !== undefined) {
            this.components.knobs.volume.setValue(params.volume);
        }
        if (this.components.wave && params.waveform !== undefined) {
            this.components.wave.setValue(params.waveform);
        }
    }

    _saveParams() {
        this.store?.setSynthParams('theremin', this.theremin.getParams());
    }

    destroy() {
        Object.values(this.components.knobs).forEach(k => k?.destroy?.());
        this.components.wave?.destroy?.();
    }
}
